import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Support.css';

const Support = () => {
  const [selectedCat, setSelectedCat] = useState('');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const categoryGridRef = useRef(null);

  // Fetch user data
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  const handleGoBack = () => {
    window.history.back();
  };

  const handleCategorySelect = (cat) => {
    setSelectedCat(cat);
    setTimeout(() => {
      document.getElementById('bodyTextarea')?.focus();
    }, 0);
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    setCharCount(value.length);
  };

  const getCharCountClass = () => {
    if (charCount > 1100) return 'tds-char-count tds-char-over';
    if (charCount > 900) return 'tds-char-count tds-char-warn';
    return 'tds-char-count';
  };

  const handleSend = async (e) => {
  e.preventDefault();

  if (!selectedCat) {
    const grid = categoryGridRef.current;
    if (grid) {
      grid.style.animation = 'none';
      void grid.offsetHeight; // trigger reflow
      grid.style.animation = 'tds-shake 0.35s ease';
    }
    return;
    }

    if (!message.trim()) {
      return;
    }

    if (!user) {
      alert('User information not available.');
      return;
    }

    setIsSubmitting(true);

    try {
      const supportRequest = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        option: selectedCat,
        message: message.trim()
      };

      const res = await axios.post(
        'https://mykaddle-api.onrender.com/api/support/submit',
        supportRequest
      );

      if (res.data.success) {
        setShowSuccess(true);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send support request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowSuccess(false);
    setMessage('');
    setCharCount(0);
    setSelectedCat('');
  };

  return (
    <div className="tds-dashboard-container">
      <div className="tds-almighty-wrapper">

        {/* Success overlay */}
        <div className={`tds-success-overlay ${showSuccess ? 'tds-show' : ''}`}>
          <div className="tds-success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#2c6530" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div className="tds-success-title">Message sent</div>
          <div className="tds-success-sub">We've received your request and will get back to you within 24 hours.</div>
          <button className="tds-back-home-btn" onClick={resetForm}>Go Back</button>
        </div>

        {/* Top Bar */}
        <div className="tds-top-bar">
          <div className="tds-top-left-header">
            <div className="tds-header-row">
              <button className="tds-back-icon-btn" aria-label="Go back" onClick={handleGoBack}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="tds-page-title">Support</span>
            </div>
            <p className="tds-sub-header">Get help with any issue within the app. For navigation guidance, read through <strong>'Help'</strong> first.</p>
          </div>

          <a href="#" className="tds-help-btn" aria-label="Open Help guide">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Help guide
          </a>
        </div>

        {/* Scrollable body */}
        <div className="tds-page-body">

          {/* Category selection */}
          <div className="tds-section-label">What can we help with?</div>
          <div className="tds-category-grid" ref={categoryGridRef}>

            <button
              type="button"
              className={`tds-cat-chip tds-payment ${selectedCat === 'payment' ? 'tds-active' : ''}`}
              onClick={() => handleCategorySelect('payment')}
            >
              <div className="tds-chip-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16">
                  <path d="M0 0h16v16H0z" fill="none" />
                  <path fill="currentColor" fillRule="evenodd" d="M11.5 14c2.49 0 4.5-1 4.5-2.5V2c0-1-2-2-4.5-2S7 1 7 2v3.5c1.17.49 2.17 1.31 2.88 2.35c.49.096 1.03.149 1.62.149c1.31 0 2.4-.261 3.18-.686a4 4 0 0 0 .323-.198v1.38c0 .235-.187.6-.802.936c-.596.325-1.51.564-2.7.564q-.357 0-.68-.027q.12.493.16 1.01q.253.014.52.014c1.31 0 2.4-.261 3.18-.686a4 4 0 0 0 .323-.198v1.38c0 .236-.149.586-.791.932c-.632.34-1.58.568-2.71.568q-.345 0-.668-.028a6.4 6.4 0 0 1-.309.974q.472.053.976.053zm2.7-7.56c.615-.336.802-.701.802-.936v-1.38q-.155.106-.323.198c-.778.425-1.87.686-3.18.686s-2.4-.261-3.18-.686a4 4 0 0 1-.323-.198v1.38c0 .235.187.6.802.935c.596.325 1.51.564 2.7.564s2.1-.239 2.7-.564zM8 2.5c0-.288.125-.565.358-.734c.127-.092.265-.184.374-.234c.273-.126 1.64-.533 2.77-.533s2.11.227 2.77.533c.124.057.261.146.382.234c.231.167.35.442.35.727v.006c0 .235-.187.6-.802.936c-.596.325-1.51.564-2.7.564s-2.1-.24-2.7-.564C8.187 3.1 8 2.734 8 2.5" clipRule="evenodd" />
                  <path fill="currentColor" fillRule="evenodd" d="M9 11.5C9 13.99 6.99 16 4.5 16S0 13.99 0 11.5S2.01 7 4.5 7S9 9.01 9 11.5m-1 0C8 13.43 6.43 15 4.5 15S1 13.43 1 11.5S2.57 8 4.5 8S8 9.57 8 11.5" clipRule="evenodd" />
                </svg>
              </div>
              <div className="tds-chip-text">
                <span className="tds-chip-title">Payment issue</span>
                <span className="tds-chip-sub">Billing, charges, refunds</span>
              </div>
              <div className="tds-chip-check">
                <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1.5 5 3.8 7.5 8.5 2.5"/>
                </svg>
              </div>
            </button>

            <button
              type="button"
              className={`tds-cat-chip ${selectedCat === 'subject' ? 'tds-active' : ''}`}
              onClick={() => handleCategorySelect('subject')}
            >
              <div className="tds-chip-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                    <path d="M18.5 17.5L21 20m-2-5a3 3 0 1 1-6 0a3 3 0 0 1 6 0" />
                    <path d="M17 22H5a2 2 0 0 1-2-2m7-2H5a2 2 0 0 0-2 2m0 0V8c0-2.828 0-4.243.879-5.121C4.757 2 6.172 2 9 2h6c1.886 0 2.828 0 3.414.586S19 4.114 19 6v3" />
                  </g>
                </svg>
              </div>
              <div className="tds-chip-text">
                <span className="tds-chip-title">Request subject</span>
                <span className="tds-chip-sub">Can't find your subject from our list?</span>
              </div>
              <div className="tds-chip-check">
                <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1.5 5 3.8 7.5 8.5 2.5"/>
                </svg>
              </div>
            </button>

            <button
              type="button"
              className={`tds-cat-chip tds-bug ${selectedCat === 'bug' ? 'tds-active' : ''}`}
              onClick={() => handleCategorySelect('bug')}
            >
              <div className="tds-chip-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <g fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M19 15v-3.062A3.94 3.94 0 0 0 15.063 8H8.936A3.94 3.94 0 0 0 5 11.938V15a7 7 0 1 0 14 0Z" />
                    <path d="M16.5 8.5v-1a4.5 4.5 0 1 0-9 0v1" />
                    <path strokeLinecap="round" d="M19 14h3M5 14H2M14.5 3.5L17 2M9.5 3.5L7 2m13.5 18l-2-.8m2-11.2l-2 .8M3.5 20l2-.8M3.5 8l2 .8M12 21.5V15" opacity=".5" />
                  </g>
                </svg>
              </div>
              <div className="tds-chip-text">
                <span className="tds-chip-title">Report a bug</span>
                <span className="tds-chip-sub">Something isn't working right</span>
              </div>
              <div className="tds-chip-check">
                <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1.5 5 3.8 7.5 8.5 2.5"/>
                </svg>
              </div>
            </button>

            <button
              type="button"
              className={`tds-cat-chip tds-user ${selectedCat === 'user' ? 'tds-active' : ''}`}
              onClick={() => handleCategorySelect('user')}
            >
              <div className="tds-chip-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 1024 1024">
                  <path d="M0 0h1024v1024H0z" fill="none" />
                  <path fill="currentColor" d="M512 512a192 192 0 1 0 0-384a192 192 0 0 0 0 384m0 64a256 256 0 1 1 0-512a256 256 0 0 1 0 512m320 320v-96a96 96 0 0 0-96-96H288a96 96 0 0 0-96 96v96a32 32 0 1 1-64 0v-96a160 160 0 0 1 160-160h448a160 160 0 0 1 160 160v96a32 32 0 1 1-64 0" />
                </svg>
              </div>
              <div className="tds-chip-text">
                <span className="tds-chip-title">Report User</span>
                <span className="tds-chip-sub">Content or behaviour concern</span>
              </div>
              <div className="tds-chip-check">
                <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1.5 5 3.8 7.5 8.5 2.5"/>
                </svg>
              </div>
            </button>

            <button
              type="button"
              className={`tds-cat-chip ${selectedCat === 'other' ? 'tds-active' : ''}`}
              onClick={() => handleCategorySelect('other')}
            >
              <div className="tds-chip-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <g fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5">
                    <path strokeLinejoin="round" d="M8 13.5h8m-8-5h4" />
                    <path d="M6.099 19q-1.949-.192-2.927-1.172C2 16.657 2 14.771 2 11v-.5c0-3.771 0-5.657 1.172-6.828S6.229 2.5 10 2.5h4c3.771 0 5.657 0 6.828 1.172S22 6.729 22 10.5v.5c0 3.771 0 5.657-1.172 6.828S17.771 19 14 19c-.56.012-1.007.055-1.445.155c-1.199.276-2.309.89-3.405 1.424c-1.563.762-2.344 1.143-2.834.786c-.938-.698-.021-2.863.184-3.865" />
                  </g>
                </svg>
              </div>
              <div className="tds-chip-text">
                <span className="tds-chip-title">Other</span>
                <span className="tds-chip-sub">Anything else on your mind</span>
              </div>
              <div className="tds-chip-check">
                <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1.5 5 3.8 7.5 8.5 2.5"/>
                </svg>
              </div>
            </button>

          </div>

          <div className="tds-divider"></div>

          {/* Detail body */}
          <form onSubmit={handleSend}>
            <div className="tds-field-group">
              <label className="tds-field-label" htmlFor="bodyTextarea">Describe your issue</label>
              <textarea
                id="bodyTextarea"
                className="tds-body-textarea"
                placeholder="Tell us what happened, any steps you took, and what you expected to see. The more detail, the faster we can help."
                maxLength="1200"
                value={message}
                onChange={handleMessageChange}
                disabled={isSubmitting}
              ></textarea>
              <div className={getCharCountClass()}>{charCount} / 1200</div>
            </div>

            {/* Send row */}
            <div className="tds-send-row">
              <p className="tds-send-note">We typically respond within 24 hours. You'll receive a reply to your registered email.</p>
              <button
                type="submit"
                className={`tds-send-btn ${isSubmitting ? 'tds-sending' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending…' : (
                  <>
                    Send request
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Support;